import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { DataType } from 'src/app/components/data-type-widgets/data-type/data-type-component';
import { SystemService } from '../../../../system/system.service';
import { FieldConfigDTO, MenuDTO, MenuType } from '../../../../system/system-dtos';
import { MaintainService } from '../maintain.service';
import { EnvironmentService } from '../../../../common/services/env-configuration/environment.service';
import { EntityDetailsMode } from './maintain-form-mode.enum';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { QueryOperand } from '../../../../common/kibana-request-dto';
import { LmTitleService } from '../../../../common/services/title.service';
import { MenuUtils } from '../../../../common/utils/menu-utils';

@Component({
  selector: 'app-maintain-form',
  templateUrl: './maintain-form.component.html',
  styleUrls: ['./maintain-form.component.scss'],
  standalone: false,
})
export class MaintainFormComponent implements OnInit {
  public mode = EntityDetailsMode.VIEW;

  public loading = true;

  public hasError = null;

  public allFields: FieldConfigDTO[];

  public fieldsToDisplay: FieldConfigDTO[];

  public data: any;

  public formGroup: UntypedFormGroup;

  @Input() code: string;

  @Input() id: string;

  @Input() groupId: string;

  @Input() entityGroupId: string;

  @Input() menuCofig: MenuDTO;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private systemService: SystemService,
    private maintainService: MaintainService,
    private envService: EnvironmentService,
    private toastrService: ToastrService,
    private lmTitleService: LmTitleService
  ) {}

  ngOnInit() {
    this.route.data.subscribe((data) => (this.mode = data.mode));
    this.route.paramMap
      .pipe(tap((pm) => (this.code = pm.get('entityCode') ?? this.route.snapshot.data['entityCodeOverride'])))
      .pipe(tap((pm) => (this.id = pm.get('entityId') ?? this.route.parent.snapshot.paramMap.get('entityId'))))
      .pipe(tap((pm) => (this.groupId = pm.get('groupId'))))
      .pipe(tap((pm) => (this.entityGroupId = pm.get('groupEntityId'))))
      .pipe(switchMap(() => this.getMenuConfig()))
      .pipe(
        switchMap((conf: MenuDTO) => {
          this.menuCofig = conf;
          if (!this.menuCofig) {
            return throwError('NO_CONF');
          }
          if (conf.menuType === MenuType.ENTITY_GROUP) {
            return throwError('ENTITY_GROUP');
          }
          this.lmTitleService.setTitleSuffix(`${this.menuCofig.label} / ${this.id}`);
          return of(conf);
        })
      )
      .pipe(tap(() => this.setAllFields()))
      .pipe(switchMap(() => this.getOne()))
      .pipe(tap((data) => (this.data = data)))
      .pipe(tap((data) => this.createFormGroupIfNeeded(data)))
      .pipe(tap(() => (this.loading = false)))
      .pipe(
        catchError((error) => {
          if (error === 'NO_CONF') {
            this.router.navigate(['/']);
          } else if (error === 'ENTITY_GROUP') {
            const defaultTab = this.menuCofig.entityGroup.menuItems[0];
            const routeBase =
              this.route.snapshot.paramMap.get('entityCode') ??
              this.route.snapshot.routeConfig?.path?.split('/')[0] ??
              this.code;
            this.router.navigateByUrl(`/${routeBase}/${this.id}/${defaultTab.code}`, { replaceUrl: true });
          }

          return EMPTY;
        })
      )
      .subscribe();
  }

  public isPanel(fieldType: DataType): boolean {
    // return fieldType === 'panel';
    return false;
  }

  private getMenuConfig(): Observable<MenuDTO> {
    if (!this.entityGroupId) {
      return this.systemService.getMenuConfig(this.code);
    }
    return this.systemService
      .getMenuConfig(this.code)
      .pipe(map((i: MenuDTO) => i.entityGroup.menuItems.find((mi) => mi.code === this.groupId)));
  }

  private getOne() {
    if (this.mode === EntityDetailsMode.ADD) {
      return of({});
    }
    const resolvedId = this.maintainService.resolveAppIdForEntity(this.getId(), this.menuCofig);
    return this.doGetOne(resolvedId);
  }

  private doGetOne(resolvedId) {
    switch (this.menuCofig.menuType) {
      case MenuType.ELASTIC:
        return this.maintainService
          .performKibanaQuery({
            perPage: 1,
            offset: 0,
            index: this.menuCofig.code,
            queryConditions: {
              index: this.menuCofig.code,
              conditions: Object.keys(resolvedId).map((k) => ({
                fieldName: EncodingUtils.encodeRequest(k),
                operand: QueryOperand.EQUALS,
                value: resolvedId[k],
              })),
            },
            orderParamTieBreaker: 'id',
          })
          .pipe(map((resp) => resp.content[0]));
      case MenuType.CASSANDRA:
        return this.maintainService
          .performCassandraQuery({
            perPage: 1,
            offset: 0,
            queryConditions: {
              index: this.menuCofig.code,
              conditions: Object.keys(resolvedId).map((k) => ({
                fieldName: EncodingUtils.encodeRequest(k),
                operand: QueryOperand.EQUALS,
                value: resolvedId[k],
              })),
            },
            orderParamTieBreaker: 'id',
          })
          .pipe(map((resp) => resp.content[0]));
      default:
        return this.maintainService.selectOne(this.parseSelectOneUrl(resolvedId));
    }
  }

  private parseSelectOneUrl(resolvedId) {
    let str = MenuUtils.getFirstMaintainConfig(this.menuCofig.entityGroup).tabs[0].endpoints.selectOneUrl;

    Object.keys(resolvedId).forEach((code) => {
      str = str.replace(`{${code}}`, resolvedId[code]);
    });

    return str;
  }

  getTitle(): string {
    return this.mode === EntityDetailsMode.ADD
      ? this.menuCofig
        ? MenuUtils.getFirstMaintainConfig(this.menuCofig.entityGroup).tabs[0].tableView.add.title
        : ''
      : MenuUtils.getFirstMaintainConfig(this.menuCofig.entityGroup).tabs[0].tableView.actions.filter(
          (a) => a.tag === this.mode
        )[0].pageTitle;
  }

  setAllFields() {
    this.allFields = MenuUtils.getFirstMaintainConfig(this.menuCofig.entityGroup)
      .rows.map((r) => r.columns)
      .reduce((acc, elem) => acc.concat(elem), [])
      .map((c) => c.fields)
      .reduce((acc, elem) => acc.concat(elem), []);

    this.fieldsToDisplay =
      this.mode === EntityDetailsMode.EDIT ? this.allFields.filter((f) => !f.editHide) : this.allFields;

    if (this.mode === EntityDetailsMode.EDIT) {
      this.fieldsToDisplay = this.allFields.filter((f) => !f.editHide);
    } else if (this.mode === EntityDetailsMode.ADD) {
      this.fieldsToDisplay = this.allFields.filter((f) => !f.addIgnore);
    } else {
      this.fieldsToDisplay = this.allFields;
    }
  }

  navigateToTable() {
    this.router.navigate([this.router.url.substr(0, this.router.url.lastIndexOf('/'))]);
  }

  submit(): void {
    if (this.mode === EntityDetailsMode.ADD) {
      this.createEntity();
    } else {
      this.editEntity();
    }
  }

  private createEntity() {
    this.maintainService
      .createEntity(
        this.formGroup.getRawValue(),
        MenuUtils.getFirstMaintainConfig(this.menuCofig.entityGroup).tabs[0].endpoints.insertUrl
      )
      .pipe(tap(() => (this.loading = true)))
      .pipe(tap(() => this.pushCreateAction()))
      .pipe(tap(() => this.navigateToTable()))
      .subscribe();
  }

  private editEntity() {
    this.maintainService
      .updateEntity(
        this.formGroup.getRawValue(),
        MenuUtils.getFirstMaintainConfig(this.menuCofig.entityGroup).tabs[0].endpoints.updateUrl.replace(
          '{id}',
          this.id
        )
      )
      .pipe(tap(() => (this.loading = true)))
      .pipe(tap(() => this.pushUpdateAction()))
      .pipe(tap(() => this.navigateToDetails()))
      .subscribe();
  }

  getDataForFormField(field: FieldConfigDTO): void {
    const { name } = field;
    return this.data[name];
  }

  private pushUpdateAction() {
    const code = this.code[0].toUpperCase() + this.code.slice(1);
    this.toastrService.success(`${code} #${this.id} has been updated!`);
  }

  private pushCreateAction() {
    const code = this.code[0].toUpperCase() + this.code.slice(1);
    this.toastrService.success(`${code} has been created!`);
  }

  private createFormGroupIfNeeded(data: any) {
    const formGroup = new UntypedFormGroup({});

    new Set([
      ...Object.keys(data),
      ...this.allFields
        .filter((f) => {
          if (this.mode === EntityDetailsMode.ADD) {
            return !f.addIgnore || !f.addIgnore;
          }
          if (this.mode === EntityDetailsMode.EDIT) {
            return !f.editHide || !f.editHide;
          }
          return true;
        })
        .map((f) => f.name),
    ]).forEach((key) => {
      const field = this.allFields.filter((f) => f.name === key);
      const disabled = field.length === 0 || field[0].editReadOnly;
      const value = data[key];
      const formControl = new UntypedFormControl({
        value,
        disabled,
      });
      formGroup.addControl(key, formControl);
    });

    this.formGroup = formGroup;
  }

  private navigateToDetails(): void {
    const { paramMap, routeConfig } = this.route.snapshot;
    const entityId = paramMap.get('entityId') ?? this.route.parent.snapshot.paramMap.get('entityId');
    const routeBase = paramMap.get('entityCode') ?? routeConfig?.path?.split('/')[0];
    const url = `/${routeBase}/${entityId}`;
    this.router.navigate([url]);
  }

  public isEditable() {
    return this.mode !== EntityDetailsMode.VIEW;
  }

  public shouldDisplayId() {
    return this.mode !== EntityDetailsMode.ADD;
  }

  private getId() {
    return this.entityGroupId ? this.entityGroupId : this.id;
  }
}
